import React, { useState } from "react";
import { Link as LinkIcon, Copy, GoogleLogo, MetaLogo, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLeadOptions } from "../lib/useLeadOptions";

/**
 * Fallback schema, used only if /meta/lead-options hasn't resolved yet.
 * The live list is served from backend/attribution.py — keep this in the same
 * shape but don't treat it as the source of truth.
 */
const FALLBACK = {
  utm: [
    { field: "utm_id", label: "Campaign ID", hint: "GA4 joins sessions to campaigns on this" },
    { field: "utm_source", label: "Source", hint: "google / meta / whatsapp" },
    { field: "utm_medium", label: "Medium", hint: "cpc / paid_social / organic" },
    { field: "utm_campaign", label: "Campaign", hint: "diwali_2026_ht" },
    { field: "utm_term", label: "Term / keyword", hint: "Paid keyword" },
    { field: "utm_content", label: "Content / creative", hint: "Ad variant" },
    { field: "utm_source_platform", label: "Source platform", hint: "Google Ads / DV360" },
    { field: "utm_creative_format", label: "Creative format", hint: "display / video / search" },
    { field: "utm_marketing_tactic", label: "Marketing tactic", hint: "remarketing / prospecting" },
  ],
  click_ids: [
    { field: "gclid", label: "gclid", platform: "google" },
    { field: "gbraid", label: "gbraid", platform: "google" },
    { field: "wbraid", label: "wbraid", platform: "google" },
    { field: "dclid", label: "dclid", platform: "google" },
    { field: "gclsrc", label: "gclsrc", platform: "google" },
    { field: "fbclid", label: "fbclid", platform: "meta" },
    { field: "msclkid", label: "msclkid", platform: "microsoft" },
    { field: "ttclid", label: "ttclid", platform: "tiktok" },
    { field: "li_fat_id", label: "li_fat_id", platform: "linkedin" },
    { field: "twclid", label: "twclid", platform: "twitter" },
    { field: "sccid", label: "ScCid", platform: "snapchat" },
    { field: "epik", label: "epik", platform: "pinterest" },
    { field: "yclid", label: "yclid", platform: "yandex" },
  ],
  ad: [
    { field: "ad_id", label: "Ad ID" },
    { field: "adset_id", label: "Adset / Ad group ID" },
    { field: "placement", label: "Placement" },
    { field: "device", label: "Device" },
    { field: "keyword", label: "Matched keyword" },
    { field: "matchtype", label: "Match type" },
    { field: "network", label: "Network" },
  ],
  page: [
    { field: "landing_page", label: "Landing page" },
    { field: "referrer", label: "Referrer" },
  ],
};

const PLATFORM_LABEL = {
  google: "Google",
  meta: "Meta",
  microsoft: "Microsoft Ads",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  twitter: "X / Twitter",
  snapchat: "Snapchat",
  pinterest: "Pinterest",
  yandex: "Yandex",
};

const PLATFORM_TONE = {
  google: "text-[#4285F4]",
  meta: "text-[#1877F2]",
  microsoft: "text-[#00A4EF]",
  tiktok: "text-[#010101]",
  linkedin: "text-[#0A66C2]",
  twitter: "text-[#000000]",
  snapchat: "text-[#FFFC00]",
  pinterest: "text-[#E60023]",
  yandex: "text-[#FC3F1D]",
};

/**
 * "Attribution" section for LeadDetail.
 *
 * Renders every non-empty UTM parameter, click ID, ad-hierarchy field and page
 * context value the lead carries, grouped so an ops person can read it without
 * knowing what a `gbraid` is. Hides itself when a lead has none (walk-ins and
 * direct calls legitimately carry zero attribution).
 */
export default function AttributionSection({ lead }) {
  const { attribution } = useLeadOptions();
  const schema = attribution || FALLBACK;
  const [showRaw, setShowRaw] = useState(false);

  const present = (list) => (list || []).filter((f) => lead[f.field]);

  const utm = present(schema.utm);
  const clickIds = present(schema.click_ids);
  const adFields = present(schema.ad);
  const pageFields = present(schema.page);
  const hasCampaign = lead.campaign_id || lead.campaign_name || lead.campaign_code;

  const hasAny =
    utm.length || clickIds.length || adFields.length || pageFields.length || hasCampaign;

  if (!hasAny) {
    return (
      <div className="border border-border bg-white" data-testid="attribution-section">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <LinkIcon size={14} />
          <h3 className="font-display text-lg font-semibold tracking-tight">Attribution</h3>
        </div>
        <div className="p-5 text-sm text-muted-foreground italic">
          No UTM tags, click IDs, or campaign attribution captured for this lead.
        </div>
      </div>
    );
  }

  const payload = () => {
    const out = {};
    ["campaign_id", "campaign_code", "campaign_name"].forEach((k) => {
      if (lead[k]) out[k] = lead[k];
    });
    [...utm, ...clickIds, ...adFields, ...pageFields].forEach(({ field }) => {
      if (lead[field]) out[field] = lead[field];
    });
    if (lead.attribution_inferred) out.attribution_inferred = lead.attribution_inferred;
    return out;
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload(), null, 2));
      toast.success("Attribution JSON copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  // Click IDs grouped by the platform that issued them.
  const byPlatform = clickIds.reduce((acc, c) => {
    const p = c.platform || "other";
    (acc[p] = acc[p] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="border border-border bg-white" data-testid="attribution-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LinkIcon size={14} />
          <h3 className="font-display text-lg font-semibold tracking-tight">Attribution</h3>
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            {Object.keys(payload()).length} fields
          </span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border hover:bg-secondary"
          >
            {showRaw ? "Grouped" : "Raw JSON"}
          </button>
          <button
            onClick={copyJson}
            data-testid="attribution-copy-json"
            className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border hover:bg-secondary inline-flex items-center gap-1"
          >
            <Copy size={11} /> Copy
          </button>
        </div>
      </div>

      {showRaw ? (
        <pre className="p-5 text-[11px] font-mono whitespace-pre-wrap break-all bg-secondary/20 overflow-x-auto">
          {JSON.stringify(payload(), null, 2)}
        </pre>
      ) : (
        <div className="p-5 space-y-5">
          {hasCampaign && (
            <Group title="Campaign">
              {lead.campaign_id && (
                <Field label="Campaign ID">
                  <span className="font-mono text-[11px]">{lead.campaign_id}</span>
                </Field>
              )}
              {lead.campaign_code && (
                <Field label="Campaign code">
                  <span className="font-mono text-[11px]">{lead.campaign_code}</span>
                </Field>
              )}
              {lead.campaign_name && (
                <Field label="Campaign name">
                  <span className="inline-block px-2 py-0.5 bg-primary/10 border border-primary/40 text-[11px] font-semibold text-primary">
                    {lead.campaign_name}
                  </span>
                </Field>
              )}
            </Group>
          )}

          {utm.length > 0 && (
            <Group title={`UTM tags · ${utm.length}/${(schema.utm || []).length}`}>
              {utm.map(({ field, label, hint }) => (
                <Field key={field} label={label} hint={hint}>
                  <span className="font-mono text-[12px]">{lead[field]}</span>
                  {lead.attribution_inferred &&
                    (field === "utm_source" || field === "utm_medium") && (
                      <span
                        title={`Inferred from ${lead.attribution_inferred} — this platform auto-tags with a click ID and sends no utm_* parameters`}
                        className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border border-amber-500 text-amber-700 bg-amber-500/10"
                        data-testid="attribution-inferred-badge"
                      >
                        Inferred
                      </span>
                    )}
                </Field>
              ))}
            </Group>
          )}

          {clickIds.length > 0 && (
            <Group title="Click IDs">
              {Object.entries(byPlatform).map(([platform, ids]) => (
                <div key={platform} className="mb-2 last:mb-0">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/70 mb-1">
                    {PLATFORM_LABEL[platform] || platform}
                  </div>
                  {ids.map(({ field, label }) => (
                    <Field
                      key={field}
                      label={label}
                      icon={
                        platform === "google"
                          ? GoogleLogo
                          : platform === "meta"
                          ? MetaLogo
                          : undefined
                      }
                      tone={PLATFORM_TONE[platform]}
                    >
                      <span className="font-mono text-[10px] break-all">{lead[field]}</span>
                    </Field>
                  ))}
                </div>
              ))}
            </Group>
          )}

          {adFields.length > 0 && (
            <Group title="Ad targeting">
              {adFields.map(({ field, label }) => (
                <Field key={field} label={label}>
                  <span className="font-mono text-[11px]">{lead[field]}</span>
                </Field>
              ))}
            </Group>
          )}

          {pageFields.length > 0 && (
            <Group title="Page context">
              {pageFields.map(({ field, label }) => (
                <Field key={field} label={label}>
                  <a
                    href={lead[field]}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-primary hover:underline break-all text-[11px]"
                  >
                    {lead[field]}
                  </a>
                </Field>
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2 flex items-center gap-1">
        {title}
      </div>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function Field({ label, hint, icon: Icon, tone, children }) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3 text-sm">
      <dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground pt-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="fill" className={tone || ""} />}
        <span>{label}</span>
        {hint && (
          <span title={hint}>
            <Info size={10} className="text-muted-foreground/60" />
          </span>
        )}
      </dt>
      <dd className="break-all">{children}</dd>
    </div>
  );
}
